/**
 * Пост-обработка поля по макету арены (ISSUE-84, `docs/ux/chibi-style-ref-2026-11-01.webp`) без полноэкранных фильтров
 * в кадре — на телефоне каждый лишний проход по экрану стоит fps:
 *
 * - tilt-shift: размытие верха и низа запекается в текстуру земли один раз на размер экрана (`bakeGround`);
 * - виньетка — один спрайт с радиальным градиентом;
 * - свечение трассеров, монет и вспышек — аддитивные спрайты одной текстуры из пула (один батч).
 *
 * Идея и часть чисел — из пробного кадра `?gfx=2` (#51, ветка `51-gfx-slice`).
 */
import type { EffectKind, ProjectileKind } from "@newfungame/contracts";
import { BlurFilter, Container, Rectangle, Sprite, Texture, type Renderer } from "pixi.js";

/** Tilt-shift: сила размытия, px; полоса — доли высоты экрана (сверху до `top`, снизу от `bottom` — в полную силу). */
const TILT = { blur: 5, top: [0.1, 0.32], bottom: [0.66, 0.9] } as const;

/** Свечение вспышек: цвет, размер в клетках (взрыв — в размерах своего кадра), яркость. */
const GLOW: Partial<Record<EffectKind, { tint: number; size: number; alpha: number }>> = {
  muzzle: { tint: 0xffd27a, size: 1.2, alpha: 0.6 },
  hit: { tint: 0xfff0b0, size: 0.8, alpha: 0.25 },
  explosion: { tint: 0xff9a3c, size: 1.4, alpha: 0.75 },
  fire: { tint: 0xff8a2c, size: 1.6, alpha: 0.7 },
  ice: { tint: 0x9fdcff, size: 1.5, alpha: 0.65 },
  lightning: { tint: 0x8fd8ff, size: 1.3, alpha: 0.75 },
  hqHit: { tint: 0xff5a3c, size: 1.8, alpha: 0.55 },
};

/** Хвост трассера: цвет и длина в клетках. */
const TRACER: Record<ProjectileKind, { tint: number; length: number }> = {
  bullet: { tint: 0xffd25a, length: 1.1 },
  cannonball: { tint: 0xffb347, length: 0.9 },
  bomb: { tint: 0xff9a3c, length: 0.5 },
  spark: { tint: 0x8fe0ff, length: 0.9 },
  flame: { tint: 0xff7a2c, length: 0.7 },
  frost: { tint: 0xbfeaff, length: 0.8 },
};

const COIN_GLOW = 0xffc93c;

function canvasTexture(w: number, h: number, paint: (g: CanvasRenderingContext2D) => void): Texture {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  paint(c.getContext("2d")!);
  return Texture.from(c);
}

/** Кольцо для эффектов на земле: белое, цвет — `tint`. */
function ringTexture(): Texture {
  return canvasTexture(128, 128, (g) => {
    g.strokeStyle = "#fff";
    g.lineWidth = 7;
    g.beginPath();
    g.arc(64, 64, 58, 0, Math.PI * 2);
    g.stroke();
  });
}

function radialTexture(stops: readonly [number, string][]): Texture {
  return canvasTexture(128, 128, (g) => {
    const r = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    for (const [at, color] of stops) r.addColorStop(at, color);
    g.fillStyle = r;
    g.fillRect(0, 0, 128, 128);
  });
}

/** Маска tilt-shift: белое — размыто, чёрное — резко (маска Pixi берёт красный канал). */
function tiltMask(): Texture {
  return canvasTexture(1, 256, (g) => {
    const l = g.createLinearGradient(0, 0, 0, 256);
    l.addColorStop(TILT.top[0], "#fff");
    l.addColorStop(TILT.top[1], "#000");
    l.addColorStop(TILT.bottom[0], "#000");
    l.addColorStop(TILT.bottom[1], "#fff");
    g.fillStyle = l;
    g.fillRect(0, 0, 1, 256);
  });
}

/**
 * Земля с tilt-shift в одну текстуру размером с экран: резкая копия, поверх — размытая под маской-полосой.
 * `raw` не трогает — его уничтожает вызывающий.
 */
export function bakeGround(renderer: Renderer, raw: Container, width: number, height: number): Texture {
  const frame = new Rectangle(0, 0, width, height);
  const resolution = renderer.resolution;
  const sharp = renderer.generateTexture({ target: raw, frame, resolution, antialias: false });
  const blur = new BlurFilter({ strength: TILT.blur, quality: 3 });
  blur.repeatEdgePixels = true;
  const blurred = new Sprite(sharp);
  blurred.filters = [blur];
  const maskTex = tiltMask();
  const mask = new Sprite(maskTex);
  mask.width = width;
  mask.height = height;
  blurred.mask = mask;
  const comp = new Container();
  comp.addChild(new Sprite(sharp), blurred, mask);
  const baked = renderer.generateTexture({ target: comp, frame, resolution, antialias: false });
  comp.destroy({ children: true });
  maskTex.destroy(true);
  blur.destroy();
  sharp.destroy(true);
  return baked;
}

/** Пул спрайтов на кадр: `next()` выдаёт следующий, `end()` прячет лишние. */
class FramePool {
  private used = 0;
  constructor(
    private readonly parent: Container,
    private readonly texture: Texture,
  ) {}
  begin(): void {
    this.used = 0;
  }
  next(): Sprite {
    let s = this.parent.children[this.used] as Sprite | undefined;
    if (!s) {
      s = new Sprite(this.texture);
      s.anchor.set(0.5);
      s.blendMode = "add";
      this.parent.addChild(s);
    }
    this.used++;
    s.visible = true;
    return s;
  }
  end(): void {
    for (let i = this.used; i < this.parent.children.length; i++) this.parent.children[i]!.visible = false;
  }
}

export interface FieldFx {
  /** Свечение: над юнитами, под полосами и цифрами. */
  glows: Container;
  vignette: Sprite;
  layout(width: number, height: number): void;
  begin(): void;
  end(): void;
  /** Вспышка эффекта; `fade` — 1 в начале жизни, 0 в конце. */
  effect(x: number, y: number, kind: EffectKind, fade: number, cell: number): void;
  /** Трассер снаряда: светлый хвост против направления полёта (`dx`, `dy` — экранные). */
  tracer(x: number, y: number, dx: number, dy: number, kind: ProjectileKind, cell: number): void;
  coin(x: number, y: number, cell: number): void;
  /** Круглое свечение произвольного цвета (перекличка героев, `intro.ts`); `size` — диаметр, px. */
  glow(x: number, y: number, size: number, tint: number, alpha: number): void;
  /** Кольцо на земле: эллипс 2:1, `radius` — по горизонтали, px. */
  ring(x: number, y: number, radius: number, tint: number, alpha: number): void;
}

export function createFieldFx(): FieldFx {
  const glowTex = radialTexture([
    [0, "rgba(255,255,255,1)"],
    [0.25, "rgba(255,255,255,0.55)"],
    [0.6, "rgba(255,255,255,0.14)"],
    [1, "rgba(255,255,255,0)"],
  ]);
  const vignetteTex = radialTexture([
    [0, "rgba(10,16,24,0)"],
    [0.6, "rgba(10,16,24,0)"],
    [1, "rgba(10,16,24,0.5)"],
  ]);
  const glows = new Container();
  const vignette = new Sprite(vignetteTex);
  vignette.anchor.set(0.5);
  const glowLayer = new Container();
  const ringLayer = new Container();
  glows.addChild(ringLayer, glowLayer);
  const pool = new FramePool(glowLayer, glowTex);
  const rings = new FramePool(ringLayer, ringTexture());

  function put(x: number, y: number, w: number, h: number, tint: number, alpha: number, rotation = 0): void {
    const s = pool.next();
    s.position.set(x, y);
    s.width = w;
    s.height = h;
    s.rotation = rotation;
    s.tint = tint;
    s.alpha = alpha;
  }

  return {
    glows,
    vignette,
    layout(width, height) {
      vignette.position.set(width / 2, height / 2);
      vignette.width = width * 1.5;
      vignette.height = height * 1.3;
    },
    begin() {
      pool.begin();
      rings.begin();
    },
    end() {
      pool.end();
      rings.end();
    },
    effect(x, y, kind, fade, cell) {
      const g = GLOW[kind];
      if (!g || fade <= 0) return;
      const size = g.size * cell;
      put(x, y - cell * 0.3, size, size, g.tint, g.alpha * Math.min(1, fade));
    },
    tracer(x, y, dx, dy, kind, cell) {
      const t = TRACER[kind];
      const len = Math.hypot(dx, dy);
      const ux = len > 0 ? dx / len : 0;
      const uy = len > 0 ? dy / len : 0;
      const tail = t.length * cell;
      // вытянутое пятно позади снаряда и яркое ядро на нём
      put(x - ux * tail * 0.4, y - uy * tail * 0.4, tail, cell * 0.28, t.tint, 0.85, Math.atan2(uy, ux));
      put(x, y, cell * 0.55, cell * 0.55, t.tint, 0.6);
    },
    coin(x, y, cell) {
      put(x, y - cell * 0.1, cell * 0.7, cell * 0.7, COIN_GLOW, 0.45);
    },
    glow(x, y, size, tint, alpha) {
      if (alpha > 0) put(x, y, size, size, tint, alpha);
    },
    ring(x, y, radius, tint, alpha) {
      if (alpha <= 0) return;
      const s = rings.next();
      s.position.set(x, y);
      s.width = radius * 2;
      s.height = radius;
      s.tint = tint;
      s.alpha = alpha;
    },
  };
}
